import React, { useEffect, useMemo, useState } from "react";
import { LockKeyhole, Save, Settings2, X } from "lucide-react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";

import { updateModuleCleContentRequest } from "../../../redux/actions/moduleCleContentActions";
import {
    getModuleCleContentUnlockModeLabel,
    MODULE_CLE_CONTENT_UNLOCK_MODE_OPTIONS,
} from "../moduleCleContentHelpers";

const buildFormFromContent = (content) => ({
    unlockMode: content?.unlockMode || "",
    unlockDate: content?.unlockDate ? content.unlockDate.slice(0, 16) : "",
    unlockStartDate: content?.unlockStartDate ? content.unlockStartDate.slice(0, 16) : "",
    unlockEndDate: content?.unlockEndDate ? content.unlockEndDate.slice(0, 16) : "",
    prerequisiteContentId: content?.prerequisiteContentId ? String(content.prerequisiteContentId) : "",
});

const toLocalDateTimeValue = (value) => {
    if (!value) return null;
    return value.length === 16 ? `${value}:00` : value;
};

export default function ModuleCleContentUnlockPanel({
    open,
    moduleCleId,
    content,
    contents,
    onClose,
}) {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.moduleCleContent || {});
    const [form, setForm] = useState(buildFormFromContent(content));

    useEffect(() => {
        if (!open) return;
        setForm(buildFormFromContent(content));
    }, [content, open]);

    const prerequisiteOptions = useMemo(
        () =>
            [...(contents || [])]
                .filter((item) => String(item.id) !== String(content?.id))
                .filter((item) => {
                    if (!content?.ordre || !item?.ordre) return true;
                    return Number(item.ordre) < Number(content.ordre);
                })
                .sort((left, right) => (left.ordre || 0) - (right.ordre || 0)),
        [content?.id, content?.ordre, contents]
    );

    if (!open || !content) return null;

    const updateField = (field, value) => setForm((previous) => ({ ...previous, [field]: value }));

    const handleModeChange = (unlockMode) => {
        setForm({
            unlockMode,
            unlockDate: "",
            unlockStartDate: "",
            unlockEndDate: "",
            prerequisiteContentId: "",
        });
    };

    const validate = () => {
        if (!form.unlockMode) return null;

        if (form.unlockMode === "BY_DATE" && !form.unlockDate) {
            return "La date de déblocage est obligatoire.";
        }

        if (form.unlockMode === "BY_PERIOD") {
            if (!form.unlockStartDate || !form.unlockEndDate) {
                return "Les dates de début et de fin sont obligatoires.";
            }
            if (new Date(form.unlockEndDate).getTime() < new Date(form.unlockStartDate).getTime()) {
                return "La date de fin doit être postérieure ou égale à la date de début.";
            }
        }

        if (form.unlockMode === "BY_PREVIOUS_CONTENT" && !form.prerequisiteContentId) {
            return "Le contenu prérequis est obligatoire.";
        }

        return null;
    };

    const handleSubmit = () => {
        const validationError = validate();
        if (validationError) {
            Swal.fire({ icon: "warning", text: validationError, timer: 2200, showConfirmButton: false });
            return;
        }

        const payload = {
            title: content.title,
            description: content.description || null,
            contentType: content.contentType,
            ordre: content.ordre,
            active: !!content.active,
            visible: !!content.visible,
            requiredContent: !!content.requiredContent,
            articleContent: content.articleContent || null,
            externalUrl: content.externalUrl || null,
            evaluationId: content.evaluationId || null,
            quizId: content.quizId || null,
            evenementId: content.evenementId || null,
            certificatId: content.certificatId || null,
            unlockMode: form.unlockMode || null,
            unlockDate: form.unlockMode === "BY_DATE" ? toLocalDateTimeValue(form.unlockDate) : null,
            unlockStartDate: form.unlockMode === "BY_PERIOD" ? toLocalDateTimeValue(form.unlockStartDate) : null,
            unlockEndDate: form.unlockMode === "BY_PERIOD" ? toLocalDateTimeValue(form.unlockEndDate) : null,
            prerequisiteContentId: form.unlockMode === "BY_PREVIOUS_CONTENT" ? Number(form.prerequisiteContentId) : null,
        };

        dispatch(
            updateModuleCleContentRequest(moduleCleId, content.id, payload, null, {
                onSuccess: () => onClose(),
            })
        );
    };

    return (
        <aside className={`mcp-panel ${open ? "is-open" : ""}`}>
            <header className="mcp-panel__header">
                <h2>
                    <LockKeyhole size={18} />
                    Conditions d'accès
                </h2>
                <button type="button" className="mcp-icon-btn" onClick={onClose}>
                    <X size={20} />
                </button>
            </header>

            <div className="mcp-panel__body">
                <section className="mcp-form-section">
                    <div className="mcp-form-section__title">Contenu cible</div>
                    <div className="mcp-info-card">
                        <strong style={{ display: "block", color: "var(--mcp-blue)", fontSize: "14px", marginBottom: "4px" }}>
                            {content.title}
                        </strong>
                        <span style={{ fontSize: "12px", color: "var(--mcp-muted)" }}>
                            Configurez comment ce contenu est débloqué pour l'apprenant.
                        </span>
                    </div>
                </section>

                <section className="mcp-form-section">
                    <div className="mcp-form-section__title">Mode de déblocage</div>
                    <div className="mcp-radio-cards" style={{ gridTemplateColumns: "1fr" }}>
                        <div
                            className={`mcp-radio-card ${!form.unlockMode ? "is-selected" : ""}`}
                            onClick={() => handleModeChange("")}
                        >
                            <strong>Déblocage immédiat</strong>
                            <span>Le contenu est accessible dès le début du module.</span>
                        </div>
                        <div
                            className={`mcp-radio-card ${form.unlockMode === "BY_DATE" ? "is-selected" : ""}`}
                            onClick={() => handleModeChange("BY_DATE")}
                        >
                            <strong>Par date précise</strong>
                            <span>Le contenu se débloque à une date et heure fixes.</span>
                        </div>
                        <div
                            className={`mcp-radio-card ${form.unlockMode === "BY_PERIOD" ? "is-selected" : ""}`}
                            onClick={() => handleModeChange("BY_PERIOD")}
                        >
                            <strong>Par période</strong>
                            <span>Le contenu n'est accessible que durant un intervalle de temps.</span>
                        </div>
                        <div
                            className={`mcp-radio-card ${form.unlockMode === "BY_PREVIOUS_CONTENT" ? "is-selected" : ""}`}
                            onClick={() => handleModeChange("BY_PREVIOUS_CONTENT")}
                        >
                            <strong>Par contenu précédent</strong>
                            <span>Nécessite la validation d'un contenu amont.</span>
                        </div>
                    </div>
                </section>

                {form.unlockMode && (
                    <section className="mcp-form-section">
                        <div className="mcp-form-section__title">Configuration du mode</div>
                        <div className="mcp-field-group">
                            {form.unlockMode === "BY_DATE" && (
                                <div className="mcp-field mcp-field-group--full">
                                    <label>Date de déblocage</label>
                                    <input
                                        type="datetime-local"
                                        value={form.unlockDate}
                                        onChange={(event) => updateField("unlockDate", event.target.value)}
                                    />
                                </div>
                            )}

                            {form.unlockMode === "BY_PERIOD" && (
                                <>
                                    <div className="mcp-field">
                                        <label>Date de début</label>
                                        <input
                                            type="datetime-local"
                                            value={form.unlockStartDate}
                                            onChange={(event) => updateField("unlockStartDate", event.target.value)}
                                        />
                                    </div>
                                    <div className="mcp-field">
                                        <label>Date de fin</label>
                                        <input
                                            type="datetime-local"
                                            value={form.unlockEndDate}
                                            onChange={(event) => updateField("unlockEndDate", event.target.value)}
                                        />
                                    </div>
                                </>
                            )}

                            {form.unlockMode === "BY_PREVIOUS_CONTENT" && (
                                <div className="mcp-field mcp-field-group--full">
                                    <label>Contenu prérequis</label>
                                    <select
                                        value={form.prerequisiteContentId}
                                        onChange={(event) => updateField("prerequisiteContentId", event.target.value)}
                                    >
                                        <option value="">- Sélectionner un contenu amont -</option>
                                        {prerequisiteOptions.map((item) => (
                                            <option key={item.id} value={String(item.id)}>
                                                #{item.ordre} - {item.title}
                                            </option>
                                        ))}
                                    </select>
                                    {prerequisiteOptions.length === 0 && (
                                        <div className="mcp-info-card" style={{ marginTop: "8px", background: "#fff7ed", borderColor: "#fed7aa", color: "#9a3412" }}>
                                            Aucun contenu précédent n'est disponible avant celui-ci.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                )}
            </div>

            <footer className="mcp-panel__footer">
                <button type="button" className="mcp-btn mcp-btn--ghost" onClick={onClose}>
                    Fermer
                </button>
                <button type="button" className="mcp-btn mcp-btn--primary" onClick={handleSubmit} disabled={loading}>
                    <Save size={16} />
                    {loading ? "Enregistrement..." : "Appliquer les conditions"}
                </button>
            </footer>
        </aside>
    );
}
